import SettingUiModal from "@app/components/setting-ui-modal";
import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import SettingUiService from "@app/services/http/setting-ui.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta } from "@app/types";
import { SettingUi, UpdateSettingUiRequest } from "@app/types/setting-ui.type";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function BgImageColumnTable({ data }: { data: SettingUi }) {
  return (
    <div className="truncate max-w-[200px] text-blue-500 hover:underline">
      <a href={data.bgImage} target="_blank" rel="noreferrer">
        {data.bgImage}
      </a>
    </div>
  );
}

function IsHeaderColumnTable({ data }: { data: SettingUi }) {
  return <div>{data.isHeader ? "Có" : "Không"}</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "10%",
    hasOrder: true,
  },
  {
    label: "Màu nền",
    dataKey: "bgColor",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Ảnh nền",
    dataKey: "bgImage",
    width: "30%",
    component: BgImageColumnTable,
    hasOrder: true,
  },
  {
    label: "Màn hình",
    dataKey: "screen",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Là header",
    dataKey: "isHeader",
    width: "20%",
    hasOrder: true,
    component: IsHeaderColumnTable,
  },
];

const SettingUiManagement = () => {
  const [settingUi, setSettingUi] = useState<SettingUi[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();

  useEffect(() => {
    SettingUiService.getList(requestParams, abortController.signal).then(
      (res) => {
        setSettingUi(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleEditClick = (settingUi: SettingUi) => {
    const modalObs = openPortalDialog(SettingUiModal, {
      settingUi,
      isEdit: true,
    });

    modalObs.afterClosed().subscribe((data) => {
      if (data?.settingUi) {
        const updateSettingUiRequest: UpdateSettingUiRequest = {
          bgColor: data.settingUi.bgColor,
          isHeader: data.settingUi.isHeader,
        };

        if (data.settingUi.hasFileChange && data.settingUi.bgImage) {
          updateSettingUiRequest.bgImage = data.settingUi.bgImage;
        }

        SettingUiService.update(settingUi.id, updateSettingUiRequest).then(
          () => {
            addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
            forceUpdate();
          }
        );
      }
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách setting UI</div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách setting UI"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={settingUi}
            hasAction2
            onEditClick={handleEditClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SettingUiManagement;
